import type { SceneDef } from "../../genesis/schema";

/**
 * Les Voyages Missionnaires de Paul — Scène 4 : la tempête et Malte.
 * Ancrage biblique : Actes 27-28. Quatorze jours de tempête sans soleil ni
 * étoiles (Ac 27:20,27) ; on jette la cargaison et les agrès à la mer
 * (drag_drop → par-dessus bord, Ac 27:18-19,38) puis les quatre ancres
 * (Ac 27:29,40) ; l'ange de Dieu promet : aucune perte de vie (Ac 27:22-24) ;
 * Paul rompt le pain et tous reprennent courage (Ac 27:33-36) ; échouage —
 * les 276 personnes parviennent TOUTES à terre saines et sauves (guide_target,
 * Ac 27:37,44) ; à Malte, le feu de l'accueil (Ac 28:2), les broussailles de
 * Paul et la vipère secouée dans le feu, sans aucun mal (drag_drop, Ac 28:3-5).
 * Interactables « avançants » (pickup + drag_drop + lever_action +
 * guide_target) : 22
 *   = 6 pickups (4 fragments du verset Ac 27:25, 2 pains du repas de courage)
 *   + 11 drag_drop (3 sacs de blé + 1 agrès + 4 ancres → par-dessus bord,
 *     2 broussailles + 1 vipère → feu de Malte)
 *   + 3 lever_action (la barre dans la tempête, le pain rompu avec actions
 *     de grâces, la sonde des matelots)
 *   + 2 guide_target (les nageurs, puis ceux sur les planches → le rivage).
 * La difficulté D/A/E scale goalCount sur ce total réel (7 / 15 / 22).
 */
export const voyagesDePaulScene4: SceneDef = {
  id: "voyages-de-paul/scene-4",
  game: "voyages-de-paul",
  title: {
    fr: "Les Voyages Missionnaires de Paul — La Tempête et Malte",
    en: "Paul's Missionary Journeys — The Storm and Malta",
  },
  order: 4,
  keyName: { fr: "Clé de la Délivrance", en: "Key of Deliverance" },
  verseBrand: "Matthieu 16:19",
  env: {
    groundSize: 68,
    skyColor: "#3d4454",
    groundColor: "#41505e",
    lighting: "dusk",
    props: [
      // Les vagues de l'Adriatique déchaînée, tout autour du navire (Ac 27:27)
      { id: "vagues", shape: "water", pos: [0, 0, -14], color: "#2b3d52", size: 2.4, scatter: 18, scatterRadius: 22 },
      // Écume blanche arrachée aux crêtes
      { id: "ecume", shape: "sphere", pos: [0, 0, -8], color: "#c7d3dd", size: 0.35, scatter: 16, scatterRadius: 20 },
      // La coque du navire d'Alexandrie chargé de blé (Ac 27:6,38)
      { id: "coque_navire", shape: "box", pos: [0, 0, -16], color: "#5a4632", size: 3, scatter: 8, scatterRadius: 12 },
      // Le grand mât et les vergues secoués par le vent Euraquilon (Ac 27:14)
      { id: "mat_vergues", shape: "cylinder", pos: [2, 0, -14], color: "#6e5a3e", size: 2.6, scatter: 3, scatterRadius: 6 },
      // Voiles déchirées claquant dans la tempête
      { id: "voiles", shape: "box", pos: [-3, 0, -19], color: "#b8b2a4", size: 1.6, scatter: 5, scatterRadius: 7 },
      // Nuages noirs : ni soleil ni étoiles pendant plusieurs jours (Ac 27:20)
      { id: "nuages", shape: "sphere", pos: [0, 14, -10], color: "#2a2f3b", size: 3, scatter: 12, scatterRadius: 24 },
      // Les rochers de la baie de Malte, au sud
      { id: "rochers_malte", shape: "mountain", pos: [-18, 0, 24], color: "#6b6258", size: 4, scatter: 6, scatterRadius: 10 },
      // La plage de sable où le navire s'échoue (Ac 27:39)
      { id: "plage_sable", shape: "sphere", pos: [0, 0, 12], color: "#c9b98c", size: 0.5, scatter: 16, scatterRadius: 14 },
      // Arbustes et broussailles de l'île (Ac 28:3)
      { id: "arbustes_malte", shape: "tree", pos: [14, 0, 24], color: "#55603f", size: 1.3, scatter: 10, scatterRadius: 9 },
      // Les habitants de l'île autour du feu, d'une bienveillance peu commune (Ac 28:2)
      { id: "insulaires", shape: "person", pos: [-4, 0, 24], color: "#8a6a4a", size: 1.3, scatter: 6, scatterRadius: 6 },
      // Étincelles du feu de l'accueil sous la pluie
      { id: "etincelles_feu", shape: "flame", pos: [2, 0, 21], color: "#e8933a", size: 0.5, scatter: 8, scatterRadius: 4 },
      // Débris et planches du navire flottant vers le rivage (Ac 27:44)
      { id: "planches_debris", shape: "box", pos: [8, 0, 6], color: "#7a6448", size: 0.9, scatter: 10, scatterRadius: 12 },
    ],
  },
  spawn: [0, 0, 0],
  difficulty: {
    // goalCount = interactables avançants accomplis (22 au total :
    // 6 pickups + 11 drag_drop + 3 lever_action + 2 guide_target).
    D: { goalCount: 7, hints: true },
    A: { goalCount: 15, hints: true },
    E: { goalCount: 22, hints: false, timerS: 540 },
  },
  interactables: [
    // ——— Tenir la barre : mécanique signature (14 nuits de tempête) ———
    {
      id: "barre_tempete",
      type: "lever_action",
      label: { fr: "La barre du navire — tiens bon à travers les quatorze nuits de tempête (Ac 27:27)", en: "The ship's helm — hold fast through the fourteen nights of storm (Acts 27:27)" },
      pos: [0, 0, -20],
      shape: "cylinder",
      color: "#e8c14a",
      size: 1.2,
      holdS: 4,
      count: 1,
    },
    {
      id: "pain_graces",
      type: "lever_action",
      label: { fr: "Le pain rompu — comme Paul, rends grâces devant tous et reprends courage (Ac 27:35-36)", en: "The broken bread — like Paul, give thanks before all and take courage (Acts 27:35-36)" },
      pos: [-4, 0, -16],
      shape: "box",
      color: "#d9b26a",
      size: 0.9,
      holdS: 3,
      count: 1,
    },
    {
      id: "sonde_marins",
      type: "lever_action",
      label: { fr: "La sonde des matelots — vingt brasses, puis quinze : la terre approche (Ac 27:28)", en: "The sailors' sounding line — twenty fathoms, then fifteen: land is near (Acts 27:28)" },
      pos: [8, 0, -8],
      shape: "cylinder",
      color: "#7d9bc4",
      size: 0.8,
      holdS: 3,
      count: 1,
    },
    // ——— Personnages nommés (npc_dialog) ———
    {
      id: "paul_navire",
      type: "npc_dialog",
      label: { fr: "Paul : « Rassurez-vous : nul de vous ne perdra la vie » (Ac 27:22)", en: "Paul: \"Cheer up: there will be no loss of life among you\" (Acts 27:22)" },
      pos: [0, 0, -12],
      shape: "person",
      color: "#e8c14a",
      size: 1.4,
    },
    {
      id: "julius",
      type: "npc_dialog",
      label: { fr: "Julius le centenier — il voulait sauver Paul et empêcha le pire (Ac 27:1,43)", en: "Julius the centurion — he wanted to save Paul and prevented the worst (Acts 27:1,43)" },
      pos: [4, 0, -16],
      shape: "person",
      color: "#8a9bb5",
      size: 1.4,
    },
    {
      id: "publius",
      type: "npc_dialog",
      label: { fr: "Publius, premier de l'île — il nous reçut trois jours avec bonté (Ac 28:7)", en: "Publius, chief of the island — he received us kindly for three days (Acts 28:7)" },
      pos: [6, 0, 22],
      shape: "person",
      color: "#6f8a6b",
      size: 1.4,
    },
    {
      id: "insulaire",
      type: "npc_dialog",
      label: { fr: "Un habitant de Malte — « ils nous montrèrent une bienveillance peu commune » (Ac 28:2)", en: "An islander of Malta — \"they showed us uncommon kindness\" (Acts 28:2)" },
      pos: [-6, 0, 22],
      shape: "person",
      color: "#b5824a",
      size: 1.35,
    },
    // ——— Par-dessus bord et feu de Malte (build_slot) ———
    {
      id: "par_dessus_bord",
      type: "build_slot",
      label: { fr: "Par-dessus bord — la zone de jet pour alléger le navire (Ac 27:18-19,38)", en: "Overboard — the jettison zone to lighten the ship (Acts 27:18-19,38)" },
      pos: [-8, 0, -12],
      shape: "water",
      color: "#28405e",
      size: 2.2,
      targetId: "par_dessus_bord",
    },
    {
      id: "feu_malte",
      type: "build_slot",
      label: { fr: "Le feu de l'accueil — les habitants l'allumèrent à cause de la pluie et du froid (Ac 28:2)", en: "The welcome fire — the islanders kindled it because of the rain and the cold (Acts 28:2)" },
      pos: [0, 0, 20],
      shape: "flame",
      color: "#e07f2e",
      size: 1.8,
      targetId: "feu_malte",
    },
    // ——— Jeter la cargaison à la mer (drag_drop, Ac 27:18,38) ———
    {
      id: "sac_ble_1",
      type: "drag_drop",
      label: { fr: "Sac de blé d'Alexandrie — jette la cargaison pour alléger le navire (Ac 27:38)", en: "Sack of Alexandrian wheat — throw the cargo out to lighten the ship (Acts 27:38)" },
      pos: [5, 0, -22],
      shape: "box",
      color: "#c2a35c",
      size: 0.7,
      targetId: "par_dessus_bord",
      count: 1,
    },
    {
      id: "sac_ble_2",
      type: "drag_drop",
      label: { fr: "Deuxième sac de blé — la vie vaut plus que la cargaison", en: "Second sack of wheat — life is worth more than cargo" },
      pos: [-5, 0, -22],
      shape: "box",
      color: "#c2a35c",
      size: 0.7,
      targetId: "par_dessus_bord",
      count: 1,
    },
    {
      id: "sac_ble_3",
      type: "drag_drop",
      label: { fr: "Troisième sac de blé — après le repas, ils jetèrent le blé à la mer (Ac 27:38)", en: "Third sack of wheat — after eating, they threw the wheat into the sea (Acts 27:38)" },
      pos: [9, 0, -18],
      shape: "box",
      color: "#b8965a",
      size: 0.7,
      targetId: "par_dessus_bord",
      count: 1,
    },
    {
      id: "agres_navire",
      type: "drag_drop",
      label: { fr: "Les agrès du navire — jetés de leurs propres mains le troisième jour (Ac 27:19)", en: "The ship's tackle — thrown out with their own hands on the third day (Acts 27:19)" },
      pos: [-9, 0, -18],
      shape: "box",
      color: "#6e5a3e",
      size: 0.8,
      targetId: "par_dessus_bord",
      count: 1,
    },
    // ——— Les quatre ancres de la poupe (drag_drop, Ac 27:29,40) ———
    {
      id: "ancre_1",
      type: "drag_drop",
      label: { fr: "Première ancre de la poupe — rends-la aux flots en attendant le jour (Ac 27:29)", en: "First stern anchor — give it back to the waves while wishing for day (Acts 27:29)" },
      pos: [12, 0, -14],
      shape: "cone",
      color: "#7e7e88",
      size: 0.8,
      targetId: "par_dessus_bord",
      count: 1,
    },
    {
      id: "ancre_2",
      type: "drag_drop",
      label: { fr: "Deuxième ancre — ils en jetèrent quatre et souhaitèrent que le jour vînt (Ac 27:29)", en: "Second anchor — they let go four and wished for daylight (Acts 27:29)" },
      pos: [-12, 0, -16],
      shape: "cone",
      color: "#7e7e88",
      size: 0.8,
      targetId: "par_dessus_bord",
      count: 1,
    },
    {
      id: "ancre_3",
      type: "drag_drop",
      label: { fr: "Troisième ancre — abandonnée à la mer au moment d'échouer (Ac 27:40)", en: "Third anchor — abandoned to the sea when running aground (Acts 27:40)" },
      pos: [12, 0, -20],
      shape: "cone",
      color: "#8a8a92",
      size: 0.8,
      targetId: "par_dessus_bord",
      count: 1,
    },
    {
      id: "ancre_4",
      type: "drag_drop",
      label: { fr: "Quatrième ancre — la dernière amarre rendue aux flots (Ac 27:40)", en: "Fourth anchor — the last mooring given back to the waves (Acts 27:40)" },
      pos: [-13, 0, -22],
      shape: "cone",
      color: "#8a8a92",
      size: 0.8,
      targetId: "par_dessus_bord",
      count: 1,
    },
    // ——— Les broussailles de Paul et la vipère (drag_drop, Ac 28:3-5) ———
    {
      id: "broussailles_1",
      type: "drag_drop",
      label: { fr: "Tas de broussailles — comme Paul, porte-le au feu de l'accueil (Ac 28:3)", en: "Bundle of sticks — like Paul, carry it to the welcome fire (Acts 28:3)" },
      pos: [-10, 0, 16],
      shape: "tree",
      color: "#6e5a3e",
      size: 0.7,
      targetId: "feu_malte",
      count: 1,
    },
    {
      id: "broussailles_2",
      type: "drag_drop",
      label: { fr: "Second fagot ramassé sous la pluie — pour que le feu ne faiblisse pas", en: "Second bundle gathered in the rain — so the fire does not die down" },
      pos: [10, 0, 16],
      shape: "tree",
      color: "#6e5a3e",
      size: 0.7,
      targetId: "feu_malte",
      count: 1,
    },
    {
      id: "vipere",
      type: "drag_drop",
      label: { fr: "La vipère accrochée à la main — secoue-la dans le feu : Paul n'en ressentit aucun mal (Ac 28:5)", en: "The viper fastened on the hand — shake it off into the fire: Paul felt no harm (Acts 28:5)" },
      pos: [3, 0, 17],
      shape: "animal",
      color: "#556b3f",
      size: 0.6,
      targetId: "feu_malte",
      count: 1,
    },
    // ——— Fragments du verset Actes 27:25 (pickup) ———
    {
      id: "frag_confiance_1",
      type: "pickup",
      label: { fr: "Fragment du verset : « J'ai cette confiance » (Ac 27:25)", en: "Verse fragment: \"For I believe\" (Acts 27:25)" },
      pos: [-16, 0, 4],
      shape: "sphere",
      color: "#f0e3b0",
      size: 0.5,
    },
    {
      id: "frag_confiance_2",
      type: "pickup",
      label: { fr: "Fragment du verset : « en Dieu » (Ac 27:25)", en: "Verse fragment: \"God\" (Acts 27:25)" },
      pos: [16, 0, 4],
      shape: "sphere",
      color: "#f0e3b0",
      size: 0.5,
    },
    {
      id: "frag_confiance_3",
      type: "pickup",
      label: { fr: "Fragment du verset : « qu'il en sera » (Ac 27:25)", en: "Verse fragment: \"that it will be\" (Acts 27:25)" },
      pos: [-20, 0, 10],
      shape: "sphere",
      color: "#f0e3b0",
      size: 0.5,
    },
    {
      id: "frag_confiance_4",
      type: "pickup",
      label: { fr: "Fragment du verset : « comme il m'a été dit » (Ac 27:25)", en: "Verse fragment: \"just as it has been told me\" (Acts 27:25)" },
      pos: [20, 0, 10],
      shape: "sphere",
      color: "#f0e3b0",
      size: 0.5,
    },
    // ——— Les pains du repas de courage (pickup, Ac 27:35-36) ———
    {
      id: "pain_1",
      type: "pickup",
      label: { fr: "Pain du repas de courage — tous, rassurés, prirent de la nourriture (Ac 27:36)", en: "Bread of the meal of courage — all cheered up and took food (Acts 27:36)" },
      pos: [-6, 0, -6],
      shape: "sphere",
      color: "#d9b26a",
      size: 0.5,
    },
    {
      id: "pain_2",
      type: "pickup",
      label: { fr: "Second pain partagé — ils étaient deux cent soixante-seize à bord (Ac 27:37)", en: "Second shared loaf — there were two hundred seventy-six on board (Acts 27:37)" },
      pos: [5, 0, -5],
      shape: "sphere",
      color: "#d9b26a",
      size: 0.5,
    },
    // ——— L'échouage : TOUS parviennent à terre (guide_target, Ac 27:43-44) ———
    {
      id: "nageurs",
      type: "guide_target",
      label: { fr: "Ceux qui savaient nager — conduis-les les premiers vers la terre (Ac 27:43)", en: "Those who could swim — lead them first toward the land (Acts 27:43)" },
      pos: [-14, 0, 0],
      shape: "person",
      color: "#c4a06a",
      size: 1.2,
      targetId: "panneau_rivage",
      count: 1,
    },
    {
      id: "naufrages_planches",
      type: "guide_target",
      label: { fr: "Ceux sur les planches et les débris — conduis-les au rivage : TOUS sains et saufs (Ac 27:44)", en: "Those on planks and pieces of the ship — lead them to shore: ALL safe (Acts 27:44)" },
      pos: [14, 0, 0],
      shape: "person",
      color: "#c4a06a",
      size: 1.2,
      targetId: "panneau_rivage",
      count: 1,
    },
    // ——— Panneaux bibliques ———
    {
      id: "panneau_ange",
      type: "panel",
      label: { fr: "Lire : l'ange de Dieu dans la tempête", en: "Read: the angel of God in the storm" },
      pos: [4, 0, -10],
      shape: "stele",
      color: "#b9a06a",
      size: 1.4,
      panelId: "p_ac27_25",
    },
    {
      id: "panneau_rivage",
      type: "panel",
      label: { fr: "Le rivage de Malte — tous parvinrent à terre sains et saufs (Ac 27:44)", en: "The shore of Malta — they all escaped safely to the land (Acts 27:44)" },
      pos: [0, 0, 26],
      shape: "stele",
      color: "#b9a06a",
      size: 1.5,
      panelId: "p_ac27_44",
    },
  ],
  objectives: [
    {
      id: "obj_alleger",
      kind: "build",
      label: { fr: "Allège le navire : jette la cargaison, les agrès et les quatre ancres (Ac 27:18-19,29,38)", en: "Lighten the ship: throw out the cargo, the tackle and the four anchors (Acts 27:18-19,29,38)" },
      target: "par_dessus_bord",
      count: 8,
    },
    {
      id: "obj_tous_sauves",
      kind: "guide",
      label: { fr: "Conduis tous les naufragés au rivage — 276 personnes, TOUTES sauvées (Ac 27:37,44)", en: "Lead all the shipwrecked to the shore — 276 souls, ALL saved (Acts 27:37,44)" },
      target: "panneau_rivage",
      count: 2,
    },
  ],
  panels: [
    {
      id: "p_ac27_25",
      title: { fr: "Confiance au cœur de la tempête", en: "Trust in the heart of the storm" },
      reference: "Actes 27:23-25",
      passage: {
        fr: "Un ange du Dieu à qui j'appartiens et que je sers m'est apparu cette nuit, et m'a dit: Paul, ne crains point; il faut que tu comparaisses devant César, et voici, Dieu t'a donné tous ceux qui naviguent avec toi. C'est pourquoi, ô hommes, rassurez-vous, car j'ai cette confiance en Dieu qu'il en sera comme il m'a été dit.",
        en: "For there stood by me this night an angel, belonging to the God whose I am and whom I serve, saying, 'Don't be afraid, Paul. You must stand before Caesar. Behold, God has granted you all those who sail with you.' Therefore, sirs, cheer up! For I believe God, that it will be just as it has been told me.",
      },
      explain: {
        fr: "Quatorze jours sans soleil ni étoiles, toute espérance de salut perdue (Ac 27:20) — et c'est le prisonnier qui relève l'équipage. Paul ne nie pas la tempête : il s'appuie sur une parole reçue de Dieu. La confiance n'est pas l'absence de vagues, c'est une promesse tenue plus fort que le vent.",
        en: "Fourteen days without sun or stars, all hope of being saved gone (Acts 27:20) — and it is the prisoner who lifts up the crew. Paul does not deny the storm: he leans on a word received from God. Trust is not the absence of waves; it is a promise held stronger than the wind.",
      },
      didYouKnow: {
        fr: "Le navire, parti trop tard dans la saison malgré l'avertissement de Paul (Ac 27:9-11), fut emporté par un vent appelé Euraquilon (Ac 27:14). Les marins jetèrent quatre ancres de la poupe et « souhaitèrent que le jour vînt » (Ac 27:29).",
        en: "The ship, sailing too late in the season despite Paul's warning (Acts 27:9-11), was caught by a wind called Euroclydon (Acts 27:14). The sailors let go four anchors from the stern and 'wished for daylight' (Acts 27:29).",
      },
      application: {
        fr: "Quelle promesse de Dieu peux-tu « tenir à la barre » cette semaine, au milieu de ta propre tempête ?",
        en: "What promise of God can you 'hold at the helm' this week, in the middle of your own storm?",
      },
    },
    {
      id: "p_ac27_44",
      title: { fr: "Tous sains et saufs", en: "All safe to land" },
      reference: "Actes 27:43-44",
      passage: {
        fr: "Mais le centenier, qui voulait sauver Paul, les empêcha d'exécuter ce dessein. Il ordonna à ceux qui savaient nager de se jeter les premiers dans l'eau pour gagner la terre, et aux autres de se mettre sur des planches ou sur des débris du navire. Et ainsi tous parvinrent à terre sains et saufs.",
        en: "But the centurion, desiring to save Paul, stopped them from their purpose, and commanded that those who could swim should throw themselves overboard first to go toward the land; and the rest should follow, some on planks, and some on other things from the ship. So they all escaped safely to the land.",
      },
      explain: {
        fr: "Le navire se brise, la cargaison est perdue — mais pas une seule vie ne manque : deux cent soixante-seize personnes, toutes à terre, exactement comme Dieu l'avait promis (Ac 27:22,37). Puis Malte : la pluie, le froid, et un feu allumé par des inconnus « d'une bienveillance peu commune » (Ac 28:2). Même la vipère ne peut rien contre la promesse (Ac 28:5) : Dieu conduit son serviteur jusqu'à Rome.",
        en: "The ship breaks apart, the cargo is lost — but not one life is missing: two hundred seventy-six people, all ashore, exactly as God had promised (Acts 27:22,37). Then Malta: rain, cold, and a fire kindled by strangers of 'uncommon kindness' (Acts 28:2). Not even the viper can prevail against the promise (Acts 28:5): God brings his servant all the way to Rome.",
      },
      didYouKnow: {
        fr: "À Malte, Paul secoua la vipère dans le feu sans ressentir aucun mal (Ac 28:5), puis pria pour le père de Publius, malade de la fièvre, qui fut guéri (Ac 28:8). Une baie de l'île porte encore aujourd'hui le nom de « baie de Saint-Paul ».",
        en: "On Malta, Paul shook the viper off into the fire and felt no harm (Acts 28:5), then prayed for Publius' father, sick with fever, and he was healed (Acts 28:8). A bay of the island still bears the name 'St. Paul's Bay' today.",
      },
      application: {
        fr: "« Dieu t'a donné tous ceux qui naviguent avec toi » (Ac 27:24) : pour qui, autour de toi, peux-tu être un porteur d'espérance dans la tempête ?",
        en: "\"God has granted you all those who sail with you\" (Acts 27:24): for whom around you can you be a bearer of hope in the storm?",
      },
    },
  ],
  quizzes: [
    {
      id: "q_promesse",
      level: "D",
      prompt: { fr: "Que promit Dieu à Paul par son ange, au cœur de la tempête ?", en: "What did God promise Paul through his angel, in the heart of the storm?" },
      choices: [
        { fr: "Que la tempête cesserait aussitôt", en: "That the storm would stop at once" },
        { fr: "Qu'aucune vie ne serait perdue parmi ceux qui naviguaient avec lui", en: "That no life would be lost among those who sailed with him" },
        { fr: "Que le navire arriverait intact au port", en: "That the ship would reach port intact" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Dieu t'a donné tous ceux qui naviguent avec toi » (Actes 27:24). Le navire fut perdu, mais pas une seule vie : la promesse portait sur les personnes, et elle s'accomplit exactement (Ac 27:44).",
        en: "\"God has granted you all those who sail with you\" (Acts 27:24). The ship was lost, but not a single life: the promise was about the people, and it was fulfilled exactly (Acts 27:44).",
      },
    },
    {
      id: "q_276",
      level: "A",
      prompt: { fr: "Combien de personnes étaient à bord — et combien furent sauvées ?", en: "How many people were on board — and how many were saved?" },
      choices: [
        { fr: "120 à bord, 100 sauvées", en: "120 on board, 100 saved" },
        { fr: "276 à bord, toutes sauvées", en: "276 on board, all saved" },
        { fr: "365 à bord, la moitié sauvée", en: "365 on board, half saved" },
        { fr: "70 à bord, toutes sauvées", en: "70 on board, all saved" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Nous étions, dans le navire, deux cent soixante-seize personnes en tout. » (Actes 27:37) « Et ainsi tous parvinrent à terre sains et saufs. » (Actes 27:44) Le compte exact de Luc souligne la fidélité exacte de Dieu.",
        en: "\"In all, we were two hundred seventy-six souls on the ship.\" (Acts 27:37) \"So they all escaped safely to the land.\" (Acts 27:44) Luke's exact count underlines God's exact faithfulness.",
      },
    },
    {
      id: "q_vipere",
      level: "E",
      prompt: { fr: "Que fit Paul lorsque la vipère, sortie des broussailles, s'attacha à sa main ?", en: "What did Paul do when the viper, driven out of the sticks, fastened on his hand?" },
      choices: [
        { fr: "Il appela les habitants à l'aide", en: "He called the islanders for help" },
        { fr: "Il secoua l'animal dans le feu et ne ressentit aucun mal", en: "He shook the creature off into the fire and felt no harm" },
        { fr: "Il quitta le feu et s'éloigna du rivage", en: "He left the fire and moved away from the shore" },
        { fr: "Il demanda à Publius un remède", en: "He asked Publius for a remedy" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Paul secoua l'animal dans le feu, et ne ressentit aucun mal. » (Actes 28:5) Les habitants s'attendaient au pire, puis changèrent d'avis (Ac 28:6) — et Dieu se servit de ce signe pour ouvrir l'île à l'Évangile : le père de Publius fut guéri, et beaucoup de malades vinrent (Ac 28:8-9).",
        en: "\"However he shook off the creature into the fire, and wasn't harmed.\" (Acts 28:5) The islanders expected the worst, then changed their minds (Acts 28:6) — and God used that sign to open the island to the Gospel: Publius' father was healed, and many sick came (Acts 28:8-9).",
      },
    },
    {
      id: "qD1",
      level: "D",
      prompt: { fr: "Quel nom portait le vent impétueux qui emporta le navire ?", en: "What was the name of the tempestuous wind that caught the ship?" },
      choices: [
        { fr: "Le Sirocco", en: "The Sirocco" },
        { fr: "L'Euraquilon", en: "Euroclydon" },
        { fr: "Le Zéphyr", en: "The Zephyr" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Mais bientôt un vent impétueux, qu'on appelle Euraquilon, se déchaîna sur l'île. » (Actes 27:14) Le navire, emporté sans pouvoir tenir contre le vent, se laissa aller à la dérive (Ac 27:15) — le début de quatorze jours d'épreuve.",
        en: "\"But before long, a tempestuous wind beat down from shore, which is called Euroclydon.\" (Acts 27:14) The ship, caught and unable to face the wind, was driven along (Acts 27:15) — the start of fourteen days of trial.",
      },
    },
    {
      id: "qD2",
      level: "D",
      prompt: { fr: "Pendant plusieurs jours de tempête, que ne voyait-on plus du tout ?", en: "During many days of storm, what could no longer be seen at all?" },
      choices: [
        { fr: "Ni le soleil ni les étoiles", en: "Neither sun nor stars" },
        { fr: "Ni la lune ni les poissons", en: "Neither moon nor fish" },
        { fr: "Ni les voiles ni le mât", en: "Neither sails nor mast" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Le soleil et les étoiles ne parurent pas pendant plusieurs jours, et la tempête était si forte que nous perdîmes enfin toute espérance de nous sauver. » (Actes 27:20) Sans soleil ni étoiles, plus aucun repère pour naviguer — c'est alors que la promesse de Dieu devint la seule boussole.",
        en: "\"When neither sun nor stars shone on us for many days, and no small storm pressed on us, all hope that we would be saved was now taken away.\" (Acts 27:20) With no sun or stars, there was nothing left to steer by — and then God's promise became the only compass.",
      },
    },
    {
      id: "qD3",
      level: "D",
      prompt: { fr: "Sur quelle île les naufragés abordèrent-ils sains et saufs ?", en: "On what island did the shipwrecked come safely ashore?" },
      choices: [
        { fr: "La Crète", en: "Crete" },
        { fr: "Chypre", en: "Cyprus" },
        { fr: "Malte", en: "Malta" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Après nous être sauvés, nous reconnûmes que l'île s'appelait Malte. » (Actes 28:1) Une baie de l'île porte encore aujourd'hui le nom de « baie de Saint-Paul » — le souvenir d'un naufrage devenu une porte pour l'Évangile.",
        en: "\"When we had escaped, then they learned that the island was called Malta.\" (Acts 28:1) A bay of the island still bears the name \"St. Paul's Bay\" today — the memory of a shipwreck that became a door for the Gospel.",
      },
    },
    {
      id: "qD4",
      level: "D",
      prompt: { fr: "Pourquoi les habitants de Malte allumèrent-ils un grand feu ?", en: "Why did the islanders of Malta kindle a great fire?" },
      choices: [
        { fr: "Pour signaler leur position aux navires", en: "To signal their position to ships" },
        { fr: "À cause de la pluie qui tombait et à cause du froid", en: "Because of the falling rain and because of the cold" },
        { fr: "Pour célébrer une fête de l'île", en: "To celebrate an island festival" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Les barbares nous témoignèrent une bienveillance peu commune; ils nous recueillirent tous auprès d'un grand feu, qu'ils avaient allumé à cause de la pluie qui tombait, et à cause du froid. » (Actes 28:2) L'hospitalité d'inconnus fut le premier visage de la providence de Dieu sur l'île.",
        en: "\"The natives showed us uncommon kindness; for they kindled a fire and received us all, because of the present rain and because of the cold.\" (Acts 28:2) The hospitality of strangers was the first face of God's providence on the island.",
      },
    },
    {
      id: "qA1",
      level: "A",
      prompt: { fr: "Dans quel ordre l'équipage allégea-t-il le navire ?", en: "In what order did the crew lighten the ship?" },
      choices: [
        { fr: "La cargaison, puis les agrès, puis le blé après le repas", en: "The cargo, then the tackle, then the wheat after the meal" },
        { fr: "Les ancres, puis les voiles, puis la cargaison", en: "The anchors, then the sails, then the cargo" },
        { fr: "Le blé, puis la cargaison, puis les agrès", en: "The wheat, then the cargo, then the tackle" },
      ],
      answerIndex: 0,
      explain: {
        fr: "Le lendemain de la tempête, on jeta la cargaison (Ac 27:18) ; le troisième jour, les agrès, « de leurs propres mains » (Ac 27:19) ; et après le repas de courage, ils jetèrent le blé à la mer (Ac 27:38). Étape après étape, on lâche ce qui pèse pour sauver ce qui compte : les vies.",
        en: "The day after the storm struck, they threw out the cargo (Acts 27:18); on the third day, the tackle, \"with their own hands\" (Acts 27:19); and after the meal of courage, they threw the wheat into the sea (Acts 27:38). Step by step, what weighs you down is let go to save what matters: the lives.",
      },
    },
    {
      id: "qA2",
      level: "A",
      prompt: { fr: "Pourquoi le centenier empêcha-t-il les soldats d'exécuter les prisonniers avant l'échouage ?", en: "Why did the centurion stop the soldiers from executing the prisoners before the ship ran aground?" },
      choices: [
        { fr: "Il craignait une révolte de l'équipage", en: "He feared a revolt of the crew" },
        { fr: "Les prisonniers avaient déjà été jugés innocents", en: "The prisoners had already been found innocent" },
        { fr: "Il voulait sauver Paul", en: "He wanted to save Paul" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Mais le centenier, qui voulait sauver Paul, les empêcha d'exécuter ce dessein. » (Actes 27:43) Julius avait appris à estimer son prisonnier (Ac 27:3) : à cause d'un seul homme fidèle, tous les prisonniers eurent la vie sauve.",
        en: "\"But the centurion, desiring to save Paul, stopped them from their purpose.\" (Acts 27:43) Julius had learned to value his prisoner (Acts 27:3): because of one faithful man, all the prisoners were spared.",
      },
    },
    {
      id: "qA3",
      level: "A",
      prompt: { fr: "Que fit Paul avant que tous ne prennent enfin de la nourriture ?", en: "What did Paul do before everyone finally took food?" },
      choices: [
        { fr: "Il distribua les dernières rations en silence", en: "He handed out the last rations in silence" },
        { fr: "Il prit du pain et rendit grâces à Dieu devant tous", en: "He took bread and gave thanks to God in the presence of all" },
        { fr: "Il ordonna un jeûne de trois jours", en: "He ordered a three-day fast" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Ayant dit cela, il prit du pain, et, après avoir rendu grâces à Dieu devant tous, il le rompit, et se mit à manger. Et tous, reprenant courage, mangèrent aussi. » (Actes 27:35-36) Un simple merci à Dieu, devant 276 personnes épuisées — et le courage revint à bord.",
        en: "\"When he had said this and had taken bread, he gave thanks to God in the presence of all; then he broke it and began to eat. Then they all cheered up, and they also took food.\" (Acts 27:35-36) A simple thanksgiving to God, before 276 exhausted people — and courage returned on board.",
      },
    },
    {
      id: "qA4",
      level: "A",
      prompt: { fr: "Qu'indiqua la sonde jetée par les matelots la quatorzième nuit ?", en: "What did the sounding line cast by the sailors show on the fourteenth night?" },
      choices: [
        { fr: "Cent brasses, puis cinquante", en: "A hundred fathoms, then fifty" },
        { fr: "Le fond restait introuvable", en: "No bottom could be found" },
        { fr: "Vingt brasses, puis quinze : la terre approchait", en: "Twenty fathoms, then fifteen: land was near" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Ayant jeté la sonde, ils trouvèrent vingt brasses; un peu plus loin, ils la jetèrent de nouveau, et trouvèrent quinze brasses. » (Actes 27:28) La profondeur qui diminuait annonçait une terre — exactement ce que Paul avait déclaré : « il faut que nous soyons jetés sur quelque île » (Ac 27:26).",
        en: "\"They took soundings, and found twenty fathoms. After a little while, they took soundings again, and found fifteen fathoms.\" (Acts 27:28) The lessening depth announced land — exactly what Paul had declared: \"we must run aground on a certain island\" (Acts 27:26).",
      },
    },
    {
      id: "qE1",
      level: "E",
      prompt: { fr: "Selon l'ange, pourquoi Paul devait-il absolument survivre à la tempête ?", en: "According to the angel, why must Paul absolutely survive the storm?" },
      choices: [
        { fr: "« Il faut que tu comparaisses devant César »", en: "\"You must stand before Caesar\"" },
        { fr: "« Tu dois rebâtir le temple de Jérusalem »", en: "\"You must rebuild the temple in Jerusalem\"" },
        { fr: "« Tu dois retourner prêcher à Athènes »", en: "\"You must return to preach in Athens\"" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Paul, ne crains point; il faut que tu comparaisses devant César, et voici, Dieu t'a donné tous ceux qui naviguent avec toi. » (Actes 27:24) Le dessein de Dieu — porter l'Évangile jusqu'à Rome (Ac 23:11) — tenait plus fort que la tempête ; et par grâce, la promesse enveloppa les 276 passagers.",
        en: "\"Don't be afraid, Paul. You must stand before Caesar. Behold, God has granted you all those who sail with you.\" (Acts 27:24) God's purpose — carrying the Gospel to Rome (Acts 23:11) — held stronger than the storm; and by grace, the promise embraced all 276 aboard.",
      },
    },
    {
      id: "qE2",
      level: "E",
      prompt: { fr: "Quand les matelots voulurent s'enfuir dans la chaloupe, que déclara Paul au centenier ?", en: "When the sailors tried to flee in the ship's boat, what did Paul declare to the centurion?" },
      choices: [
        { fr: "« Laissez-les partir, Dieu les protégera »", en: "\"Let them go, God will protect them\"" },
        { fr: "« Si ces hommes ne restent pas dans le navire, vous ne pouvez être sauvés »", en: "\"Unless these stay in the ship, you can't be saved\"" },
        { fr: "« Suivons-les tous dans la chaloupe »", en: "\"Let us all follow them into the boat\"" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Paul dit au centenier et aux soldats: Si ces hommes ne restent pas dans le navire, vous ne pouvez être sauvés. » (Actes 27:31) La promesse de Dieu n'annulait pas la responsabilité de chacun : les soldats coupèrent les cordes de la chaloupe (Ac 27:32), et tous restèrent ensemble — jusqu'au salut de tous.",
        en: "\"Paul said to the centurion and to the soldiers, 'Unless these stay in the ship, you can't be saved.'\" (Acts 27:31) God's promise did not cancel each one's responsibility: the soldiers cut the ropes of the boat (Acts 27:32), and all stayed together — until all were saved.",
      },
    },
    {
      id: "qE3",
      level: "E",
      prompt: { fr: "Que fit Paul pour le père de Publius, malade de la fièvre et de la dysenterie ?", en: "What did Paul do for the father of Publius, sick with fever and dysentery?" },
      choices: [
        { fr: "Il lui prépara un remède avec des herbes de l'île", en: "He prepared a remedy for him with herbs of the island" },
        { fr: "Il l'envoya chercher un médecin à bord", en: "He sent him to find a physician on board" },
        { fr: "Il pria, lui imposa les mains, et le guérit", en: "He prayed, laid his hands on him, and healed him" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Paul, s'étant rendu vers lui, pria, lui imposa les mains, et le guérit. » (Actes 28:8) Publius avait reçu les naufragés trois jours avec bonté (Ac 28:7) ; la guérison ouvrit l'île entière : « les autres malades de l'île vinrent aussi, et ils furent guéris » (Ac 28:9). L'hospitalité et la grâce se répondent.",
        en: "\"Paul entered in to him, prayed, and laying his hands on him, healed him.\" (Acts 28:8) Publius had kindly received the shipwrecked for three days (Acts 28:7); the healing opened the whole island: \"the rest also who had diseases in the island came and were cured\" (Acts 28:9). Hospitality and grace answer one another.",
      },
    },
    {
      id: "qD5",
      level: "D",
      prompt: { fr: "Vers quel pays le navire de Paul faisait-il voile ?", en: "For what country was Paul's ship sailing?" },
      choices: [
        { fr: "Vers l'Égypte", en: "For Egypt" },
        { fr: "Vers l'Italie", en: "For Italy" },
        { fr: "Vers l'Espagne", en: "For Spain" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Lorsqu'il fut décidé que nous nous embarquerions pour l'Italie, on remit Paul et quelques autres prisonniers à un centenier… nommé Julius. » (Actes 27:1) Prisonnier ayant fait appel à César (Ac 25:11-12), Paul voguait vers Rome — et la tempête même ne détourna pas ce cap.",
        en: "\"When it was determined that we should sail for Italy, they delivered Paul and certain other prisoners to a centurion named Julius.\" (Acts 27:1) A prisoner who had appealed to Caesar (Acts 25:11-12), Paul was sailing for Rome — and not even the storm turned that course aside.",
      },
    },
    {
      id: "qD6",
      level: "D",
      prompt: { fr: "Combien de temps les naufragés restèrent-ils sur l'île de Malte ?", en: "How long did the shipwrecked stay on the island of Malta?" },
      choices: [
        { fr: "Trois mois", en: "Three months" },
        { fr: "Trois jours", en: "Three days" },
        { fr: "Trois ans", en: "Three years" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Après un séjour de trois mois, nous nous embarquâmes sur un navire d'Alexandrie, qui avait passé l'hiver dans l'île. » (Actes 28:11) Trois mois d'hiver, transformés en saison de grâce : guérisons, honneurs rendus, et provisions données au départ (Ac 28:9-10).",
        en: "\"After three months, we set sail in a ship of Alexandria which had wintered in the island.\" (Acts 28:11) Three winter months turned into a season of grace: healings, honors given, and supplies provided at departure (Acts 28:9-10).",
      },
    },
    {
      id: "qD7",
      level: "D",
      prompt: { fr: "Que pensèrent d'abord les habitants de Malte en voyant la vipère à la main de Paul ?", en: "What did the islanders of Malta first think when they saw the viper on Paul's hand?" },
      choices: [
        { fr: "Qu'il était un dieu descendu vers eux", en: "That he was a god come down to them" },
        { fr: "Qu'il était un magicien puissant", en: "That he was a powerful magician" },
        { fr: "Que c'était certainement un meurtrier que la justice poursuivait", en: "That he was certainly a murderer whom Justice pursued" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Certainement cet homme est un meurtrier, puisque la Justice n'a pas voulu le laisser vivre, après qu'il a été sauvé de la mer. » (Actes 28:4) Puis, le voyant sans aucun mal, ils dirent que c'était un dieu (Ac 28:6). Deux jugements hâtifs — la vérité était ailleurs : un serviteur gardé par la promesse de son Dieu.",
        en: "\"No doubt this man is a murderer, whom, though he has escaped from the sea, yet Justice has not allowed to live.\" (Acts 28:4) Then, seeing him unharmed, they said he was a god (Acts 28:6). Two hasty judgments — the truth lay elsewhere: a servant kept by his God's promise.",
      },
    },
    {
      id: "qA5",
      level: "A",
      prompt: { fr: "Quel port l'équipage tentait-il d'atteindre en quittant Beaux-Ports ?", en: "What harbor was the crew trying to reach when they left Fair Havens?" },
      choices: [
        { fr: "Phénix, un port de Crète, pour y passer l'hiver", en: "Phoenix, a harbor of Crete, to winter there" },
        { fr: "Syracuse, pour y vendre le blé", en: "Syracuse, to sell the wheat there" },
        { fr: "Césarée, pour y livrer les prisonniers", en: "Caesarea, to deliver the prisoners there" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« La plupart furent d'avis de partir de Beaux-Ports, dans l'espérance d'atteindre Phénix, port de Crète… afin d'y passer l'hiver. » (Actes 27:12) Paul avait pourtant averti du danger (Ac 27:9-10) ; on préféra l'avis du pilote — et l'Euraquilon les surprit en chemin (Ac 27:14).",
        en: "\"The majority advised to set sail from Fair Havens, if by any means they could reach Phoenix, a port of Crete, and winter there.\" (Acts 27:12) Paul had warned of the danger (Acts 27:9-10); they preferred the pilot's advice — and Euroclydon caught them on the way (Acts 27:14).",
      },
    },
    {
      id: "qA6",
      level: "A",
      prompt: { fr: "Quelle enseigne portait le navire d'Alexandrie qui les emmena après l'hiver ?", en: "What figurehead did the Alexandrian ship that carried them after the winter bear?" },
      choices: [
        { fr: "L'aigle de Rome", en: "The eagle of Rome" },
        { fr: "Les Dioscures, Castor et Pollux", en: "The Twin Brothers, Castor and Pollux" },
        { fr: "La déesse Artémis", en: "The goddess Artemis" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Nous nous embarquâmes sur un navire d'Alexandrie, qui avait passé l'hiver dans l'île, et qui portait pour enseigne les Dioscures. » (Actes 28:11) Castor et Pollux étaient les protecteurs païens des marins ; Luc note le détail avec un sourire : la vraie protection venait d'ailleurs (Ac 27:24).",
        en: "\"We set sail in a ship of Alexandria which had wintered in the island, whose sign was 'The Twin Brothers.'\" (Acts 28:11) Castor and Pollux were the pagan protectors of sailors; Luke notes the detail with a smile: the true protection had come from elsewhere (Acts 27:24).",
      },
    },
    {
      id: "qA7",
      level: "A",
      prompt: { fr: "Depuis combien de temps les passagers étaient-ils à jeun quand Paul les invita à manger ?", en: "For how long had the passengers gone without food when Paul urged them to eat?" },
      choices: [
        { fr: "Trois jours", en: "Three days" },
        { fr: "Sept jours", en: "Seven days" },
        { fr: "Quatorze jours", en: "Fourteen days" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« C'est aujourd'hui le quatorzième jour que vous êtes dans l'attente et à jeun, sans avoir rien pris. Je vous invite donc à prendre de la nourriture, car cela est nécessaire pour votre salut. » (Actes 27:33-34) Paul veille sur les âmes ET sur les corps : la foi n'oublie pas le pain.",
        en: "\"Today is the fourteenth day that you wait and continue fasting, having taken nothing. Therefore I beg you to take some food, for this is for your safety.\" (Acts 27:33-34) Paul watches over souls AND bodies: faith does not forget bread.",
      },
    },
    {
      id: "qE4",
      level: "E",
      prompt: { fr: "Sous quel prétexte les matelots mirent-ils la chaloupe à la mer pour s'enfuir ?", en: "Under what pretense did the sailors lower the ship's boat to flee?" },
      choices: [
        { fr: "Sous prétexte de jeter les ancres de la proue", en: "Under pretense of laying out anchors from the bow" },
        { fr: "Pour aller sonder la profondeur de la baie", en: "To go and sound the depth of the bay" },
        { fr: "Pour porter un message à la terre", en: "To carry a message to the land" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Mais, comme les matelots cherchaient à s'échapper du navire, et mettaient la chaloupe à la mer sous prétexte de jeter les ancres de la proue… » (Actes 27:30) Paul déjoua la ruse (Ac 27:31), et les soldats coupèrent les cordes de la chaloupe (Ac 27:32) : le salut promis passait par le fait de rester ensemble.",
        en: "\"As the sailors were trying to flee out of the ship and had lowered the boat into the sea, pretending that they would lay out anchors from the bow…\" (Acts 27:30) Paul exposed the ruse (Acts 27:31), and the soldiers cut the ropes of the boat (Acts 27:32): the promised salvation required staying together.",
      },
    },
    {
      id: "qE5",
      level: "E",
      prompt: { fr: "Que fit Paul en voyant les frères venus à sa rencontre au Forum d'Appius et aux Trois-Tavernes ?", en: "What did Paul do when he saw the brothers who came to meet him at the Market of Appius and The Three Taverns?" },
      choices: [
        { fr: "Il pleura sur Jérusalem", en: "He wept over Jerusalem" },
        { fr: "Il rendit grâces à Dieu et prit courage", en: "He thanked God and took courage" },
        { fr: "Il renvoya son escorte romaine", en: "He sent away his Roman escort" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Les frères… vinrent à notre rencontre jusqu'au Forum d'Appius et aux Trois-Tavernes. Paul, en les voyant, rendit grâces à Dieu, et prit courage. » (Actes 28:15) Après la tempête, la vipère et la longue route, le visage des frères fut la dernière provision de Dieu avant Rome.",
        en: "\"The brothers… came to meet us as far as The Market of Appius and The Three Taverns. When Paul saw them, he thanked God and took courage.\" (Acts 28:15) After the storm, the viper and the long road, the faces of the brothers were God's last provision before Rome.",
      },
    },
    {
      id: "qE6",
      level: "E",
      prompt: { fr: "Quelle parole du Seigneur, reçue à Jérusalem, annonçait déjà que Paul irait à Rome ?", en: "What word of the Lord, received at Jerusalem, had already announced that Paul would go to Rome?" },
      choices: [
        { fr: "« Tu seras conduit jusqu'en Espagne »", en: "\"You will be brought as far as Spain\"" },
        { fr: "« Tu ne verras plus jamais la mer »", en: "\"You will never see the sea again\"" },
        { fr: "« De même que tu as rendu témoignage de moi dans Jérusalem, il faut aussi que tu rendes témoignage dans Rome »", en: "\"As you have testified about me at Jerusalem, so you must testify also at Rome\"" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Le Seigneur apparut à Paul, et dit: Prends courage; car, de même que tu as rendu témoignage de moi dans Jérusalem, il faut aussi que tu rendes témoignage dans Rome. » (Actes 23:11) La tempête d'Actes 27 ne pouvait engloutir cette parole : l'ange la redit dans la nuit (Ac 27:24), et Paul arriva bien à Rome (Ac 28:16).",
        en: "\"The Lord stood by him and said, 'Cheer up, Paul, for as you have testified about me at Jerusalem, so you must testify also at Rome.'\" (Acts 23:11) The storm of Acts 27 could not swallow that word: the angel repeated it in the night (Acts 27:24), and Paul did arrive in Rome (Acts 28:16).",
      },
    },
    {
      id: "qD8",
      level: "D",
      prompt: { fr: "Quel compagnon macédonien de Thessalonique naviguait avec Paul et Luc dans cette traversée ?", en: "Which Macedonian companion from Thessalonica was sailing with Paul and Luke on this voyage?" },
      choices: [
        { fr: "Timothée", en: "Timothy" },
        { fr: "Aristarque", en: "Aristarchus" },
        { fr: "Tite", en: "Titus" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Nous nous embarquâmes… ayant avec nous Aristarque, Macédonien de Thessalonique. » (Actes 27:2) Déjà entraîné dans l'émeute du théâtre d'Éphèse (Ac 19:29), Aristarque partagea aussi la tempête et le naufrage : la fidélité discrète d'un compagnon, jusqu'au bout du voyage.",
        en: "\"Embarking… Aristarchus, a Macedonian of Thessalonica, being with us.\" (Acts 27:2) Already dragged into the theater riot at Ephesus (Acts 19:29), Aristarchus also shared the storm and the shipwreck: the quiet faithfulness of a companion, to the very end of the journey.",
      },
    },
    {
      id: "qE7",
      level: "E",
      prompt: { fr: "Après avoir hissé la chaloupe, pourquoi les marins ceignirent-ils le navire de câbles et se laissèrent-ils aller à la dérive ?", en: "After hoisting up the boat, why did the sailors undergird the ship with cables and let themselves be driven along?" },
      choices: [
        { fr: "Pour naviguer plus vite vers l'Italie", en: "To sail faster toward Italy" },
        { fr: "Pour préparer un abordage", en: "To prepare for boarding another ship" },
        { fr: "Ils craignaient d'échouer sur les bancs de sable de la Syrte", en: "They feared running aground on the Syrtis sand bars" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Ils employèrent les cordages pour ceindre le navire, et, dans la crainte de tomber sur la Syrte, ils abaissèrent les voiles. C'est ainsi qu'ils se laissèrent emporter par le vent. » (Actes 27:17) La Syrte, redoutables bancs de sable au large de l'Afrique, terrifiait les marins ; mais le vent qui les emportait les menait en réalité vers Malte — et vers Rome, selon la promesse (Ac 27:24).",
        en: "\"They used cables to help reinforce the ship. Fearing that they would run aground on the Syrtis sand bars, they lowered the sea anchor, and so were driven along.\" (Acts 27:17) The Syrtis, dreaded sand banks off the African coast, terrified the sailors; yet the wind driving them was in fact carrying them to Malta — and on to Rome, according to the promise (Acts 27:24).",
      },
    },
  ],
  tips: [
    {
      fr: "Sur le pont au nord : tiens la barre à travers la tempête, jette la sonde, puis fais glisser les trois sacs de blé, les agrès et les quatre ancres vers la zone « par-dessus bord » (Ac 27:18-19,29,38).",
      en: "On the deck to the north: hold the helm through the storm, cast the sounding line, then drag the three wheat sacks, the tackle and the four anchors to the 'overboard' zone (Acts 27:18-19,29,38).",
    },
    {
      fr: "Avant l'échouage, maintiens le pain rompu avec actions de grâces et ramasse les deux pains : tous reprirent courage (Ac 27:35-36). Puis conduis les nageurs, et ensuite ceux des planches, jusqu'au rivage.",
      en: "Before running aground, hold the broken bread with thanksgiving and pick up the two loaves: all took courage (Acts 27:35-36). Then lead the swimmers, and next those on planks, to the shore.",
    },
    {
      fr: "À Malte au sud : porte les deux fagots de broussailles au feu de l'accueil, et secoue la vipère dans les flammes — sans aucun mal, comme Paul (Ac 28:3-5).",
      en: "On Malta to the south: carry the two bundles of sticks to the welcome fire, and shake the viper off into the flames — without any harm, like Paul (Acts 28:3-5).",
    },
  ],
  lesson: {
    fr: "J'ai cette confiance en Dieu qu'il en sera comme il m'a été dit. (Actes 27:25)",
    en: "For I believe God, that it will be just as it has been told me. (Acts 27:25)",
  },
  audio: { ambience: "storm" },
  multiplayer: { mode: "coop", maxPlayers: 4 },
};

export default voyagesDePaulScene4;
